/**
 * Add Control Plane Resource Instance Form Composer
 *
 * Form for creating resource instances with cascading path prefix logic.
 * When user selects a resource type, the path field auto-populates with the type's path definition.
 *
 * Key Features:
 * - Resource type dropdown filtered to platform/azure only (excludes user types)
 * - Path field auto-populates when resource type is selected
 * - User can edit the entire path field after it's populated
 */

import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  AddControlPlaneResourceInstanceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddControlPlaneResourceInstanceFormData
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import {
  renderTextControl,
  renderTextareaControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Component Props
// ============================================================================

export type AddControlPlaneResourceInstanceComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddControlPlaneResourceInstanceFormData>
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Field validation via Zod schema (onBlur only)
// ============================================================================

const validateField = (name: keyof AddControlPlaneResourceInstanceFormData, value: unknown): string | null => {
  const shape = AddControlPlaneResourceInstanceFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// ============================================================================
// Composer Component
// ============================================================================

export function AddControlPlaneResourceInstanceComposer(props: AddControlPlaneResourceInstanceComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    return ['resourceTypeNumber', 'instanceName', 'instanceDescription', 'instancePath', 'instanceAccessPolicy'].includes(name)
  }

  // State to track selected resource type for cascading logic
  const [, setSelectedResourceTypeNumber] = React.useState<number | undefined>(
    initialValues?.resourceTypeNumber
  )

  // Fetch resource types with filtering to platform/azure only
  const { optionsMap, loadingMap, rawData } = useSelectOptionsMap({
    resourceTypeNumber: {
      endpoint: 'getResourceTypes',
      dataKey: 'resourceTypes',
      valueField: 'resourceTypeNumber',
      labelField: 'resourceTypeName',
      // Filter to only platform/azure (exclude user)
      dependentTransformer: (data) =>
        data
          .filter((rt: any) =>
            rt.resourceTypeCategory === 'platform' ||
            rt.resourceTypeCategory === 'azure'
          )
          .map((rt: any) => ({
            value: String(rt.resourceTypeNumber),
            label: rt.resourceTypeName || `Type ${rt.resourceTypeNumber}`,
          }))
    }
  })

  const resourceTypesData = rawData?.resourceTypeNumber || []

  const upsertResourceInstance = useApiMutation('upsertControlPlaneResourceInstance', 'POST', {
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to create resource instance:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: AddControlPlaneResourceInstanceFormSchema,
    defaultValues: {
      instanceNumber: initialValues?.instanceNumber,
      resourceTypeNumber: initialValues?.resourceTypeNumber ?? ('' as any),
      instanceName: initialValues?.instanceName ?? '',
      instanceDescription: initialValues?.instanceDescription ?? '',
      instancePath: initialValues?.instancePath ?? '',
      instanceAccessPolicy: initialValues?.instanceAccessPolicy ?? '',
    },
    onSubmit: async ({ value }) => {
      const formValue = value as AddControlPlaneResourceInstanceFormData
      // Validate with Zod schema (single source of truth)
      const parsed = AddControlPlaneResourceInstanceFormSchema.safeParse({
        instanceNumber: initialValues?.instanceNumber,
        resourceTypeNumber: formValue.resourceTypeNumber ? Number(formValue.resourceTypeNumber) : 0,
        instanceName: formValue.instanceName?.trim() ?? '',
        instanceDescription: formValue.instanceDescription?.trim() ?? '',
        instancePath: formValue.instancePath?.trim() ?? '',
        instanceAccessPolicy: formValue.instanceAccessPolicy?.trim() ?? '',
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as any, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      // Transform and submit
      const apiRequest = transformToApiRequest(parsed.data)
      await upsertResourceInstance.mutateAsync(apiRequest)
    },
  })

  // Handle resource type selection change - update path prefix
  const handleResourceTypeChange = (value: string) => {
    const numValue = value ? Number(value) : undefined
    setSelectedResourceTypeNumber(numValue)

    if (numValue && resourceTypesData.length > 0) {
      const selectedType = resourceTypesData.find(
        (rt: any) => rt.resourceTypeNumber === numValue
      )
      if (selectedType && selectedType.resourceTypePathDefinition) {
        // Update path with prefix from resource type definition + backslash
        const pathPrefix = `${selectedType.resourceTypePathDefinition}\\`
        form.setFieldValue('instancePath', pathPrefix as any)
      }
    } else {
      form.setFieldValue('instancePath', '' as any)
    }
  }

  // Render a single control with explicit render function
  const renderControl = (control: any) => {
    const isRequired = isRequiredField(control.name)

    // Special handling for resource type select field
    if (control.name === 'resourceTypeNumber') {
      return (
        <form.AppField
          key={control.name}
          name={control.name}
          validators={{
            onBlur: ({ value }) => validateField(control.name as keyof AddControlPlaneResourceInstanceFormData, value) || undefined,
            onChange: ({ value }) => {
              // Handle cascading logic on change
              handleResourceTypeChange(value as string)
              return undefined
            },
          }}
        >
          {(bound) => {
            const config: ControlConfig = {
              name: control.name,
              label: control.label,
              placeholder: control.placeholder,
              description: control.description,
              required: isRequired,
              visibility: control.visibility ?? true,
              disabled: control.disabled ?? false,
              options: optionsMap.resourceTypeNumber,
              isLoading: loadingMap.resourceTypeNumber,
            }

            const { SelectField } = bound
            return (
              <SelectField
                label={config.label}
                required={config.required}
                placeholder={config.isLoading ? 'Loading...' : config.placeholder || ''}
                options={config.options || []}
                description={config.description}
                disabled={config.disabled}
              />
            )
          }}
        </form.AppField>
      )
    }

    // Standard field rendering for other fields
    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) => validateField(control.name as keyof AddControlPlaneResourceInstanceFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            description: control.description,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }

          // Select appropriate render function based on control type
          if (control.htmlControl === 'textarea') {
            return renderTextareaControl(config, bound)
          } else {
            return renderTextControl(config, bound)
          }
        }}
      </form.AppField>
    )
  }

  // Render form content with groups
  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []

      if (groupControls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">
              {FORM_CONFIG.formDescription}
            </p>
          </div>
        )}

        {renderFormContent()}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}

          {renderButtonControl({
            label: upsertResourceInstance.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? '')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? ''),
            variant: 'default',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}

// Re-exports
export {
  AddControlPlaneResourceInstanceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddControlPlaneResourceInstanceFormData
} from './schema'
